import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { UserProfileData, UserProfile } from '../datatypes/user-profile';

import { UserProfileContext } from '../contexts/user-profile-context';

import { ContextProvider } from '@lit/context';

import { resolveRouterPath, router } from '../router';
import { ApiFactory } from '../api/agwasuite-api-factory';
import { clearTabSessionId } from '../utility/session-storage-keys';
import { ApiLogin } from '../api/core/agwasuite-api-login';

import { setActiveUserProfile } from '../active-userprofile';

import { SSEClient } from '../utility/notifications/sse-client';
import { groupUserIdsCacheInstance } from '../global-data/group-user-ids-cache';
import { sessionManager } from '../services/session-manager';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const WithUserProfile = <T extends new (...args: any[]) => LitElement>(
  superClass: T
) => {
  class WithUserProfileClass extends superClass {
    // Ensure mixin-compatible constructor signature
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(...args: any[]) {
      super(...args);
    }
    private _provider = new ContextProvider(this, {
      context: UserProfileContext,
    });
    private _navigateOnUpdate = true;
    @property({ attribute: false })
    private _userProfile?: UserProfile | undefined;
    private _refreshTimer?: ReturnType<typeof setTimeout>;
    public get userProfile(): UserProfile | undefined {
      return this._userProfile;
    }
    public set userProfile(value: UserProfile | undefined) {
      const previousUserId = this._userProfile?.data?.userId;
      this._userProfile = value;
      ApiFactory.setAuthToken(undefined, value?.data?.userId ?? '');
      //using cookie

      this._provider.setValue(value);
      setActiveUserProfile(value);

      if (this._navigateOnUpdate) {
        if (value) {
          //    router.navigate(resolveRouterPath('')); // updated navigation
        } else {
          setTimeout(() => {
            void router?.navigate(resolveRouterPath('login')); // updated navigation
          }, 1000);
        }
      }
      // The company's people list belongs to the person who was allowed to read it, so it is
      // forgotten when the person changes or signs out. A renewal of the same person keeps it,
      // and keeps the watch of a picker open on the page. Nothing is fetched here: the list is
      // read only by its one consumer, and only for a person who may read the roster (#398 C5).
      if (value?.data?.userId !== previousUserId) {
        groupUserIdsCacheInstance().stopAll();
      }
    }

    /**
     * @deprecated Profile images are now managed via UserDirectory cache.
     * This method is kept for interface compatibility but is a no-op.
     */
    async updateUserProfilePicture(_image: string) {
      // Profile images are now fetched from UserDirectory, not stored locally.
      // The server sends an SSE 'user-updated' event that updates the cache.
      this.requestUpdate();
    }
    async setUserProfile(profile: UserProfile | undefined) {
      if (!profile) {
        this._refreshTimer && clearTimeout(this._refreshTimer);
        this._refreshTimer = undefined;
      }
      // User profile is now purely in-memory; server is source of truth via verify()
      // Session ID and user ID are stored in sessionStorage for per-tab isolation
      this.userProfile = profile;
      // Refresh SSE connection context (switches to user or anonymous mode). Not awaited: this
      // setter is synchronous and the connect carries on in the background, as it always has.
      void SSEClient.getInstance().connectAppropriate();
      this.requestUpdate();
    }

    async loadAndVerifyUserProfile() {
      // The ACCOUNT key, deliberately - this asks "is an account signed in on this tab", which is
      // what that key answers. It is not the lock-ownership session (#362) and must not become it.
      const accountKey = ApiFactory.getAccountKey();

      if (!accountKey) {
        // No account on this tab - user is not logged in
        this.userProfile = undefined;
        this.requestUpdate();
        return;
      }

      // Create an authenticated API client using the session ID (cookie auth)
      const api = ApiFactory.authenticated(ApiLogin);
      // Call the API to verify the session's credentials
      const response = await api.verify();

      if (response.success && response.data.loginCredentials) {
        const creds = response.data.loginCredentials;

        // Build profile from server response (server is source of truth)
        const profileData: UserProfileData = {
          authProvider: 'internal',
          username: creds.username,
          displayName: creds.displayName,
          emailAddress: creds.emailAddress ?? undefined,
          token: undefined, // Using httpOnly cookies
          expiresUtc: creds.expiresUtc,
          isTwoFactorEnabled: creds.isTwoFactorEnabled,
          claims: {},
          userId: creds.loginUserId,
          loginGroupId: creds.loginGroupId ?? undefined,
          loginGroupName: creds.loginGroupName ?? undefined,
          securityContext: creds.securityContext ?? undefined,
        };

        const userProfile = new UserProfile(profileData);

        // Store userId in sessionStorage for CSRF header (per-tab)
        ApiFactory.setAuthToken(undefined, creds.loginUserId);

        if (userProfile.isLoggedIn) {
          this.userProfile = userProfile;
        } else {
          this.userProfile = undefined;
        }
      } else {
        // Verification failed - only clear session if server explicitly rejected it (401/403)
        // Network errors (status 0) or server errors (5xx) should preserve session for retry
        const isAuthRejection =
          response.status === 401 || response.status === 403;
        if (isAuthRejection) {
          this.signOutAfterRefusal();
        }
        // For network errors or server errors, keep session intact - user can retry when server is back
      }

      // If a valid userProfile exists, set up token refresh timer
      if (this.userProfile) {
        await this.refrehUserToken();
      }
      // Notify the UI that state has changed
      this.requestUpdate();
    }
    /**
     * The tab's sign-out when the SERVER has refused its sign-in: forget the profile, the account
     * keys and the tab session, and (through the userProfile setter) go to the sign-in page. No
     * request to the server, because there is no sign-in left for one to end.
     *
     * One path for both places that learn of a refusal: the boot-time verify above, and the
     * connection-health monitor's sign-in check (D-137, rule 4), which calls this rather than
     * keeping a second sign-out of its own. For a check that named somebody else, app-index's handler
     * first ends this tab's session on the server and calls this afterwards (D-143,
     * sign-out-after-mismatch.ts), because there the server has NOT ended anything.
     */
    signOutAfterRefusal() {
      this.userProfile = undefined;
      // Clear invalid session from storage
      sessionStorage.removeItem('agwa-active-account');
      sessionStorage.removeItem('agwa-active-userid');
      // And this tab's session. The server refused us, so whatever session we held can no
      // longer be proved; keeping the id would make every later request quote a session that
      // answers for nobody. The next authenticated boot asks for a fresh one (#366).
      clearTabSessionId();
      this.requestUpdate();
    }
    async refrehUserToken() {
      if (!this.userProfile) return;

      if (!this.userProfile.needsRefresh()) {
        const expiresIn = this.userProfile.expiresAtMilliseconds();
        const refreshTime = expiresIn - 3600000; // 1 hour before expiry
        if (refreshTime > 0) {
          this._refreshTimer = setTimeout(
            () => this.renewUserToken(),
            refreshTime
          );
        }
      } else {
        await this.renewUserToken();
      }
    }
    async logout() {
      if (!this.userProfile) return;

      // Get the current session ID from session manager
      const currentSessionId = sessionManager.currentSessionId;

      if (currentSessionId) {
        // Use session-aware logout that only removes this session's cookie
        await sessionManager.logoutSession(currentSessionId);
      } else {
        // Fallback: use legacy logout (clears all cookies)
        const api = ApiFactory.authenticated(ApiLogin);
        await api.logout();
      }

      await this.clearUserProfile();
      // After logout, maintain anonymous heartbeat. An anonymous connect issues no session, so
      // there is nothing here to wait for.
      void SSEClient.getInstance().connectAppropriate();
      // Explicitly ensure all group listeners removed
      groupUserIdsCacheInstance().stopAll();
    }
    async renewUserToken() {
      if (!this.userProfile) return;
      const api = ApiFactory.authenticated(ApiLogin);
      const response = await api.renew();
      if (response.success && response.data?.loginCredentials) {
        const creds = response.data.loginCredentials;

        // Update userId in sessionStorage
        ApiFactory.setAuthToken(undefined, creds.loginUserId);

        // Build profile from server response (server is source of truth)
        const profileData: UserProfileData = {
          authProvider: 'internal',
          username: creds.username,
          displayName: creds.displayName,
          emailAddress: creds.emailAddress ?? undefined,
          token: undefined, // Using httpOnly cookies
          expiresUtc: creds.expiresUtc,
          isTwoFactorEnabled: creds.isTwoFactorEnabled,
          claims: {},
          userId: creds.loginUserId,
          loginGroupId: creds.loginGroupId ?? undefined,
          loginGroupName: creds.loginGroupName ?? undefined,
          securityContext: creds.securityContext ?? undefined,
        };

        const userProfile = new UserProfile(profileData);
        await this.setUserProfile(userProfile);
      } else {
        await this.clearUserProfile();
      }
    }

    async clearUserProfile() {
      await this.userProfile?.performLogoutOperations();
      this.userProfile = undefined;
      // Clear session data from sessionStorage (per-tab)
      sessionStorage.removeItem('agwa-active-account');
      sessionStorage.removeItem('agwa-active-userid');
      // The sign-out request has already ENDED this tab's session server-side and released its
      // locks, so forgetting the id here is the second half of one act rather than a cleanup.
      // Clearing it WITHOUT the server having ended it would strand the row: still live, still
      // holding its locks, and now unreachable by the only tab that could release them.
      clearTabSessionId();
      ApiFactory.setAuthToken(undefined, '');
      this.requestUpdate();
      // Defensive cleanup
      groupUserIdsCacheInstance().stopAll();
    }
  }

  return WithUserProfileClass as Constructor<WithUserProfileInterface> & T;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Constructor<T> = new (...args: any[]) => T;

export interface WithUserProfileInterface {
  userProfile?: UserProfile;
  loadAndVerifyUserProfile(): Promise<void>;
  signOutAfterRefusal(): void;
  setUserProfile(profile: UserProfile | undefined): Promise<void>;
  logout(): Promise<void>;
  updateUserProfilePicture(image: string): Promise<void>;
}
